<template>
  <div>
    <BlockUI v-if="loading"></BlockUI>
    <TituloComponent icono-titulo="fas fa-file-excel" texto-titulo="Actualización masiva de precios"></TituloComponent>
    
    <div class="main-card mb-3 card col-12">
      <div class="card-body">
        <h5 class="card-title">Subir archivo Excel</h5>
        <p class="text-muted mb-3">
          Seleccione un archivo Excel (.xls o .xlsx) con los precios actualizados de los artículos.
        </p>
        
        <div class="custom-file mb-3">
          <input
            type="file"
            class="custom-file-input"
            id="archivoExcel"
            @change="handleFileChange"
            accept=".xls,.xlsx"
            :disabled="loading"
          />
          <label class="custom-file-label" for="archivoExcel">
            {{ nombreArchivo || 'Seleccionar archivo...' }}
          </label>
        </div>

        <button
          class="btn btn-info"
          @click="procesarArchivo"
          :disabled="!archivo || loading"
        >
          <i class="fas fa-upload mr-2"></i>
          Procesar archivo
        </button>
      </div>
    </div>

    <!-- Resultados de la actualización -->
    <div v-if="resultado" class="main-card mb-3 card col-12">
      <div class="card-body">
        <div v-if="resultado.success">
          <div class="alert alert-success" role="alert">
            <i class="fas fa-check-circle mr-2"></i>
            {{ resultado.message }}
          </div>

          <div class="row mb-3">
            <div class="col-md-6">
              <div class="card bg-success text-white">
                <div class="card-body">
                  <h5 class="card-title">
                    <i class="fas fa-check mr-2"></i>
                    Artículos Actualizados
                  </h5>
                  <h2 class="mb-0">{{ resultado.total_actualizados }}</h2>
                </div>
              </div>
            </div>
            <div class="col-md-6">
              <div class="card bg-warning text-white">
                <div class="card-body">
                  <h5 class="card-title">
                    <i class="fas fa-exclamation-triangle mr-2"></i>
                    No Encontrados
                  </h5>
                  <h2 class="mb-0">{{ resultado.total_no_encontrados }}</h2>
                </div>
              </div>
            </div>
          </div>

          <!-- Tabla de artículos actualizados -->
          <div v-if="resultado.articulos_actualizados && resultado.articulos_actualizados.length > 0" class="mb-4">
            <h5 class="mb-3">
              <i class="fas fa-list mr-2"></i>
              Detalle de artículos actualizados
            </h5>
            <div class="table-responsive">
              <table class="table table-hover table-striped table-bordered">
                <thead class="thead-light">
                  <tr>
                    <th>Código</th>
                    <th>Descripción</th>
                    <th>% Aumento</th>
                    <th>Precio Venta Anterior</th>
                    <th>Precio Venta Nuevo</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(articulo, index) in resultado.articulos_actualizados" :key="index">
                    <td>{{ articulo.codigo }}</td>
                    <td>{{ articulo.descripcion }}</td>
                    <td>{{ articulo.porcentaje_aumento }}%</td>
                    <td>${{ articulo.precio_venta_anterior }}</td>
                    <td><strong class="text-success">${{ articulo.precio_venta_nuevo }}</strong></td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <!-- Tabla de artículos no encontrados -->
          <div v-if="resultado.articulos_no_encontrados && resultado.articulos_no_encontrados.length > 0">
            <h5 class="mb-3">
              <i class="fas fa-exclamation-circle mr-2"></i>
              Artículos no encontrados
            </h5>
            <div class="table-responsive">
              <table class="table table-hover table-striped table-bordered">
                <thead class="thead-light">
                  <tr>
                    <th>Código</th>
                    <th>Nombre en archivo</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(articulo, index) in resultado.articulos_no_encontrados" :key="index">
                    <td>{{ articulo.codigo }}</td>
                    <td>{{ articulo.nombre }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <button class="btn btn-primary mt-3" @click="resetFormulario">
            <i class="fas fa-plus mr-2"></i>
            Procesar otro archivo
          </button>
        </div>

        <!-- Error -->
        <div v-else class="alert alert-danger" role="alert">
          <h5 class="alert-heading">
            <i class="fas fa-times-circle mr-2"></i>
            {{ resultado.message }}
          </h5>
          <p v-if="resultado.error" class="mb-0">{{ resultado.error }}</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import TituloComponent from "@/components/layouts/TituloComponent.vue";
import BlockUI from "@/components/loaders/BlockUI.vue";
import axios from "axios";
import Swal from "sweetalert2";

export default {
  name: "ActualizacionMasiva",
  components: {
    TituloComponent,
    BlockUI,
  },
  data() {
    return {
      archivo: null,
      nombreArchivo: "",
      loading: false,
      resultado: null,
    };
  },
  methods: {
    handleFileChange(event) {
      const file = event.target.files[0];
      if (file) {
        // Validar que sea un archivo Excel
        const extension = file.name.split('.').pop().toLowerCase();
        if (extension !== 'xls' && extension !== 'xlsx') {
          Swal.fire({
            icon: "error",
            title: "Archivo no válido",
            text: "Por favor, seleccione un archivo Excel (.xls o .xlsx)",
          });
          event.target.value = '';
          this.archivo = null;
          this.nombreArchivo = "";
          return;
        }
        
        this.archivo = file;
        this.nombreArchivo = file.name;
      } else {
        this.archivo = null;
        this.nombreArchivo = "";
      }
    },
    
    async procesarArchivo() {
      if (!this.archivo) {
        Swal.fire({
          icon: "warning",
          title: "Atención",
          text: "Por favor, seleccione un archivo antes de continuar",
        });
        return;
      }

      this.loading = true;
      this.resultado = null;

      try {
        const formData = new FormData();
        formData.append('archivo', this.archivo);

        const response = await axios.post('/precios/actualizacion-masiva', formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });

        // El backend devuelve un array con un objeto
        if (Array.isArray(response.data) && response.data.length > 0) {
          this.resultado = response.data[0];
        } else {
          this.resultado = response.data;
        }

        if (this.resultado.success) {
          Swal.fire({
            icon: "success",
            title: "¡Éxito!",
            text: this.resultado.message,
          });
        } else {
          Swal.fire({
            icon: "error",
            title: "Error",
            text: this.resultado.message,
          });
        }
      } catch (error) {
        console.error('Error al procesar el archivo:', error);
        
        let mensajeError = 'No se pudo procesar el archivo. Por favor, verifique el formato e intente nuevamente.';
        
        if (error.response && error.response.data) {
          if (Array.isArray(error.response.data) && error.response.data.length > 0) {
            const errorData = error.response.data[0];
            if (errorData.message) {
              mensajeError = errorData.message;
              if (errorData.error) {
                mensajeError += ': ' + errorData.error;
              }
            }
          } else if (error.response.data.message) {
            mensajeError = error.response.data.message;
          }
        }
        
        Swal.fire({
          icon: "error",
          title: "Error",
          text: mensajeError,
        });
      } finally {
        this.loading = false;
      }
    },
    
    resetFormulario() {
      this.archivo = null;
      this.nombreArchivo = "";
      this.resultado = null;
      
      // Limpiar el input file
      const fileInput = document.getElementById('archivoExcel');
      if (fileInput) {
        fileInput.value = '';
      }
    }
  },
};
</script>

<style scoped>
.custom-file-label::after {
  content: "Buscar";
}

.table-responsive {
  max-height: 500px;
  overflow-y: auto;
}

.card-body h2 {
  font-size: 2.5rem;
  font-weight: bold;
}

.bg-success .card-title,
.bg-warning .card-title {
  font-size: 1.1rem;
  margin-bottom: 0.5rem;
}
</style>
